import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Link } from 'react-router';
import {
  Search,
  SearchX,
  X,
  Check,
  AlertCircle,
  AlertTriangle,
  CloudOff,
  Info,
  Loader2,
  Lock,
  Minus,
} from 'lucide-react';

/* =====================================================================
   Design system — componentes base (SaaS Visual UX skill §42)
   ===================================================================== */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

export function Button({
  variant = 'secondary',
  size,
  loading,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: 'sm';
  loading?: boolean;
}) {
  return (
    <button
      type="button"
      {...rest}
      disabled={rest.disabled || loading}
      className={`btn btn--${variant}${size === 'sm' ? ' btn--sm' : ''}${
        rest.className ? ' ' + rest.className : ''
      }`}
    >
      {loading && <span className="spinner" aria-hidden />}
      {children}
    </button>
  );
}

export function IconButton({
  label,
  danger,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; danger?: boolean }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      {...rest}
      className={`icon-btn${danger ? ' icon-btn--danger' : ''}${
        rest.className ? ' ' + rest.className : ''
      }`}
    >
      {children}
    </button>
  );
}

/* ---------------------------------------------------------------- campos */

export function Field({
  label,
  required,
  error,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string | null;
  hint?: string;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {required && (
          <span className="field__req" aria-hidden>
            *
          </span>
        )}
      </label>
      {children(id)}
      {hint && !error && <span className="field__hint">{hint}</span>}
      {error && (
        <span className="field__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`input${props.className ? ' ' + props.className : ''}`} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea {...props} className={`textarea${props.className ? ' ' + props.className : ''}`} />
  );
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`select${props.className ? ' ' + props.className : ''}`} />;
}

/** Busca neutra em repouso, borda grafite no foco. Debounce no chamador. */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Buscar…',
  'aria-label': ariaLabel = 'Buscar',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  'aria-label'?: string;
}) {
  return (
    <div className="search">
      <Search size={15} className="search__icon" aria-hidden />
      <input
        type="search"
        className="search__input"
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/* ---------------------------------------------------------------- modal */

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
  dirty,
  acoes,
  variante,
  subtitulo,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /** Com dado não salvo, clicar no overlay NÃO fecha (skill §16.2). */
  dirty?: boolean;
  /** Botões no cabeçalho, à esquerda do fechar. */
  acoes?: ReactNode;
  /** `documento` ocupa ~90% da tela e vira tela cheia no celular. */
  variante?: 'documento';
  /** Linha fina abaixo do título: tipo, número, revisão, situação. */
  subtitulo?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>(
      'input,select,textarea,button,[tabindex]:not([tabindex="-1"])',
    )?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !dirty) {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !ref.current) return;
      // focus trap
      const items = [
        ...ref.current.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
        ),
      ].filter((el) => el.offsetParent !== null);
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey, true);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [onClose, dirty]);

  return (
    <div
      className="modal__overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !dirty) onClose();
      }}
    >
      <div
        className={`modal${wide ? ' modal--wide' : ''}${
          variante ? ` modal--${variante}` : ''
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={ref}
      >
        <div className="modal__header">
          <div className="modal__identificacao">
            <h2 className="modal__title" id={titleId}>
              {title}
            </h2>
            {subtitulo && <p className="modal__subtitulo">{subtitulo}</p>}
          </div>
          {acoes && <div className="modal__acoes">{acoes}</div>}
          <IconButton label="Fechar" onClick={onClose}>
            <X size={18} />
          </IconButton>
        </div>
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__footer">{footer}</div>}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- progresso */

type Tone = 'ok' | 'run' | 'warn' | 'danger' | 'muted';

const TONE_ICON: Record<Tone, ReactNode> = {
  ok: <Check size={12} strokeWidth={3} aria-hidden />,
  run: <Loader2 size={12} strokeWidth={3} aria-hidden className="spin" />,
  warn: <AlertTriangle size={12} strokeWidth={3} aria-hidden />,
  danger: <X size={12} strokeWidth={3} aria-hidden />,
  muted: <Minus size={12} strokeWidth={3} aria-hidden />,
};

/**
 * Marcador curto com ícone na frente — "Lote aplicado", "Sincronizando",
 * "3 rejeitadas".
 *
 * O ícone é quem carrega o estado. A cor é reforço: em escala de cinza, ou
 * para quem não separa verde de vermelho, o check e o X continuam dizendo
 * coisas diferentes.
 */
export function StatusPill({
  tone = 'muted',
  icon,
  children,
  plain,
}: {
  tone?: Tone;
  icon?: ReactNode;
  children: ReactNode;
  plain?: boolean;
}) {
  return (
    <span className={`pill pill--${tone}${plain ? ' pill--plain' : ''}`}>
      <span className="pill__icon">{icon ?? TONE_ICON[tone]}</span>
      {children}
    </span>
  );
}

/** Círculo de estado para lista e linha do tempo de etapas. */
export function StateDot({ tone, label }: { tone: Tone | 'idle'; label: string }) {
  return (
    <span className={`state-dot state-dot--${tone}`} role="img" aria-label={label}>
      {tone === 'idle' ? <Minus size={13} strokeWidth={3} aria-hidden /> : TONE_ICON[tone]}
    </span>
  );
}

/**
 * Barra de progresso fina (4px).
 *
 * Sem `value` ela vira indeterminada: um pedaço que atravessa, para quando o
 * sistema sabe que está trabalhando mas não sabe quanto falta. Fingir uma
 * porcentagem nesse caso é mentir para o usuário.
 */
export function ProgressBar({
  value,
  label,
  showValue,
}: {
  value?: number | null;
  label: string;
  showValue?: boolean;
}) {
  const indeterminate = value === null || value === undefined;
  const pct = indeterminate ? 0 : Math.max(0, Math.min(100, Math.round(value)));

  const bar = (
    <span
      className={`progress${indeterminate ? ' progress--indeterminate' : ''}${
        pct === 100 ? ' progress--done' : ''
      }`}
      role="progressbar"
      aria-label={label}
      aria-valuenow={indeterminate ? undefined : pct}
      aria-valuemin={indeterminate ? undefined : 0}
      aria-valuemax={indeterminate ? undefined : 100}
      style={{ '--progress-value': `${pct}%` } as Record<string, string>}
    >
      <span className="progress__fill" />
    </span>
  );

  if (!showValue) return bar;
  return (
    <span className="progress-row">
      {bar}
      <span className="progress-row__value">{indeterminate ? '—' : `${pct}%`}</span>
    </span>
  );
}

/**
 * Fio de carregamento global, embaixo da topbar.
 *
 * Um indicador só para a aplicação inteira, em vez de um spinner em cada
 * canto: a tela para de tremer e ainda assim ninguém fica sem saber que o
 * sistema está buscando dado.
 */
export function LoadBar({ active }: { active: boolean }) {
  if (!active) return null;
  return <div className="loadbar" role="status" aria-label="Carregando" />;
}

/* ---------------------------------------------------------------- feedback */

export type SaveState = 'idle' | 'saving' | 'saved' | 'pending-sync' | 'error' | 'conflict';

/**
 * "Salvo" SÓ aparece depois do ACK real do backend.
 *
 * Cada estado tem ícone próprio, e é o ícone que diz o que aconteceu: giro
 * para trabalhando, check para gravado, nuvem cortada para "está no aparelho
 * e ainda não subiu", X para falhou.
 */
export function SaveFeedback({ state, error }: { state: SaveState; error?: string | null }) {
  if (state === 'idle') return null;
  if (state === 'saving')
    return (
      <span role="status">
        <StatusPill tone="run">Salvando…</StatusPill>
      </span>
    );
  if (state === 'saved')
    return (
      <span role="status" className="rise">
        <StatusPill tone="ok">Salvo</StatusPill>
      </span>
    );
  if (state === 'pending-sync')
    return (
      <span role="status">
        <StatusPill tone="warn" icon={<CloudOff size={12} strokeWidth={2.5} aria-hidden />}>
          No aparelho · aguardando sinal
        </StatusPill>
      </span>
    );
  if (state === 'conflict')
    return (
      <span role="alert">
        <StatusPill tone="danger" icon={<AlertCircle size={12} strokeWidth={2.5} aria-hidden />}>
          Alterado por outra pessoa
        </StatusPill>
      </span>
    );
  return (
    <span role="alert">
      <StatusPill tone="danger">{error || 'Não foi possível salvar'}</StatusPill>
    </span>
  );
}

export function Banner({
  kind = 'info',
  children,
  action,
}: {
  kind?: 'info' | 'warn' | 'danger';
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={`banner banner--${kind}`} role={kind === 'danger' ? 'alert' : 'status'}>
      {children}
      {action && (
        <>
          <span className="banner__spacer" />
          {action}
        </>
      )}
    </div>
  );
}

/**
 * Faixa fina de aviso global (skill §14). Aviso de sistema não é cartão
 * gigante: é uma linha no topo, com ícone e ação opcional.
 */
export function AlertBar({
  kind = 'info',
  icon,
  children,
  action,
}: {
  kind?: 'info' | 'warn' | 'danger';
  icon?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={`alert-bar alert-bar--${kind}`} role={kind === 'danger' ? 'alert' : 'status'}>
      {icon}
      <span className="truncate">{children}</span>
      {action && (
        <>
          <span className="alert-bar__spacer" />
          {action}
        </>
      )}
    </div>
  );
}

/**
 * Arte de estado vazio (skill §22): máquina com proteção e engrenagem, em
 * traço, na paleta do sistema. Existe para explicar — aparece só no primeiro
 * uso, nunca em busca sem resultado nem em erro.
 */
function EmptyArt() {
  return (
    <svg
      className="empty__art"
      width="96"
      height="64"
      viewBox="0 0 96 64"
      fill="none"
      aria-hidden
      focusable="false"
    >
      <rect
        x="6.5"
        y="14.5"
        width="55"
        height="38"
        rx="3"
        stroke="var(--border-strong)"
        strokeWidth="1.5"
      />
      <path d="M6.5 24.5h55" stroke="var(--border-strong)" strokeWidth="1.5" />
      <path
        d="M14 52V33m9 19V33m9 19V33m9 19V33m9 19V33"
        stroke="var(--n-300)"
        strokeWidth="1.5"
      />
      <circle cx="75" cy="26" r="11.5" stroke="var(--brand)" strokeWidth="1.5" />
      <circle cx="75" cy="26" r="4" stroke="var(--brand)" strokeWidth="1.5" />
      <path
        d="M75 8v6m0 24v6m-18-18h6m24 0h6M62 13l4 4m18 18 4 4m0-26-4 4M66 35l-4 4"
        stroke="var(--brand)"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path d="M69 56h34" stroke="var(--heat)" strokeWidth="0" />
      <rect x="70" y="46" width="10" height="10" rx="2" fill="var(--heat-soft)" />
      <path d="M72.5 51h5" stroke="var(--heat)" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Vazio não é um estado só (skill §23). Primeiro uso pede ilustração e
 * convite; busca sem resultado pede o texto do filtro; erro e falta de
 * permissão não podem parecer "está vazio".
 */
export function EmptyState({
  title,
  hint,
  action,
  variant = 'search',
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
  variant?: 'first' | 'search' | 'error' | 'forbidden';
}) {
  return (
    <div className={`empty${variant === 'first' ? ' empty--first' : ''}`}>
      {variant === 'first' ? (
        <EmptyArt />
      ) : variant === 'error' ? (
        <AlertCircle size={24} aria-hidden color="var(--danger)" />
      ) : variant === 'forbidden' ? (
        <Lock size={24} aria-hidden />
      ) : (
        <SearchX size={24} aria-hidden />
      )}
      <div className="empty__title">{title}</div>
      {hint && <div className="empty__hint">{hint}</div>}
      {action && <div className="empty__cta">{action}</div>}
    </div>
  );
}

/**
 * Barra de proporção. A cor entra por token do chamador, porque num gráfico
 * de risco ela significa a banda (skill §27) — não é cor de série.
 */
export function Meter({
  value,
  max,
  color,
  label,
}: {
  value: number;
  max: number;
  color?: string;
  label: string;
}) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <span
      className="meter"
      role="img"
      aria-label={label}
      style={
        { '--meter-value': `${pct}%`, '--meter-color': color } as Record<string, string | undefined>
      }
    >
      <span className="meter__fill" />
    </span>
  );
}

/** Cartão de KPI (skill §26): número grande, rótulo pequeno, sem decoração. */
export function KpiCard({
  label,
  value,
  note,
  to,
  tone,
}: {
  label: string;
  value: ReactNode;
  note?: string;
  to?: string;
  tone?: 'danger' | 'warn';
}) {
  const body = (
    <>
      <span className="kpi__label">{label}</span>
      <span className={`kpi__value${tone ? ` kpi__value--${tone}` : ''}`}>{value}</span>
      {note && <span className="kpi__note">{note}</span>}
    </>
  );
  if (!to) return <div className="kpi">{body}</div>;
  return (
    <Link to={to} className="kpi kpi--link">
      {body}
    </Link>
  );
}

/**
 * Escolha entre poucas opções exclusivas, dentro da toolbar única
 * (skill §15). Botão com aria-pressed, não rádio disfarçado.
 */
export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string; count?: number }[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className="segmented__item"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          {o.count !== undefined && <span className="segmented__count">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

/**
 * Ajuda contextual (skill §24): módulo complexo ganha "Informações", com
 * resumo curto, de três a cinco passos, e um "Entendi" para sair.
 */
export function HelpButton({
  title,
  summary,
  steps,
  label = 'Informações',
}: {
  title: string;
  summary: string;
  steps: ReactNode[];
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="help-btn" onClick={() => setOpen(true)}>
        <Info size={16} aria-hidden />
        {label}
      </button>
      {open && (
        <Modal
          title={title}
          onClose={() => setOpen(false)}
          footer={
            <Button variant="primary" onClick={() => setOpen(false)}>
              Entendi
            </Button>
          }
        >
          <div className="stack">
            <p className="muted">{summary}</p>
            <ol className="help-steps">
              {steps.map((s, i) => (
                <li key={i}>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </div>
        </Modal>
      )}
    </>
  );
}

export function ListSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="list" aria-busy="true" aria-label="Carregando">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="row">
          <div className="skeleton" style={{ height: 14, width: `${40 + ((i * 13) % 40)}%` }} />
        </div>
      ))}
    </div>
  );
}

/**
 * As seis faixas da metodologia HRN versionada no banco. Só elas têm cor.
 *
 * Esta lista não é decoração: é o contrato entre o CSS e a engenharia.
 * Ver `risk_methodology_versions` e docs/RISCO-VOCABULARIOS.md.
 */
const FAIXAS_HRN = ['Raro', 'Baixo', 'Atenção', 'Significativo', 'Alto', 'Extremo'] as const;

/**
 * Faixa de risco.
 *
 * Em produção convivem DOIS vocabulários: o da metodologia HRN e o que veio
 * das planilhas do cliente ("Significante", "Muito Alto", "Não aplicável").
 * Não existe equivalência aprovada entre eles.
 *
 * Por isso o que não está na escala HRN aparece com o **texto original** e um
 * marcador NEUTRO. Pintar "Muito Alto" de vermelho seria o sistema afirmando
 * uma classificação técnica que nenhum responsável técnico assinou — e é
 * exatamente o tipo de invenção que um laudo não pode conter.
 */
export function RiskBand({
  band,
  escala = 'desconhecida',
}: {
  band: string | null;
  /**
   * De qual escala este rótulo veio.
   *
   * `hrn` — calculado pelo sistema com a metodologia versionada. Recebe cor.
   * `legado` — veio da planilha do cliente. Fica neutro.
   * `desconhecida` — não dá para afirmar. Fica neutro, por segurança.
   */
  escala?: 'hrn' | 'legado' | 'desconhecida';
}) {
  if (!band) return <span className="muted">—</span>;

  // Só pinta o que o próprio sistema calculou pela escala aprovada.
  // A palavra sozinha NÃO basta: "Baixo" vale 1–5 no HRN e 1–50 no legado,
  // e "Alto" vale 100–500 no HRN e 100–150 no legado. Colorir pelo rótulo
  // pintaria de verde uma NC que na escala oficial seria "Atenção".
  if (escala === 'hrn' && (FAIXAS_HRN as readonly string[]).includes(band)) {
    return (
      <span className="risk-band" data-band={band}>
        {band}
      </span>
    );
  }

  return (
    <span
      className="risk-band risk-band--sem-mapa"
      title={
        `"${band}" veio da planilha de origem. As faixas do cronograma legado são ` +
        `deslocadas em relação à metodologia HRN — a mesma palavra cobre intervalos ` +
        `diferentes —, então o valor é exibido como está, sem cor de classificação.`
      }
    >
      {band}
      <span className="risk-band__nota" aria-hidden>
        origem
      </span>
      <span className="sr-only">
        — classificação da planilha de origem, sem equivalência aprovada na escala HRN
      </span>
    </span>
  );
}

export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="toolbar">{children}</div>;
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page__header">
      <div className="inline" style={{ justifyContent: 'space-between' }}>
        <div>
          <h1 className="page__title">{title}</h1>
          {subtitle && <p className="page__subtitle">{subtitle}</p>}
        </div>
        {actions}
      </div>
    </header>
  );
}

export function SectionHeader({ title, count }: { title: string; count?: ReactNode }) {
  return (
    <div className="section-header">
      <h2 className="section-header__title">{title}</h2>
      {count !== undefined && <span className="section-header__count">{count}</span>}
    </div>
  );
}

/** Debounce 300ms — não dispara busca pesada a cada tecla (SKILL §7.3). */
export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Media query reativa.
 *
 * Ler `window.innerWidth` uma vez no render NÃO funciona: a tela não volta ao
 * layout de desktop quando o usuário gira o celular ou redimensiona a janela.
 * `matchMedia` + listener resolve, e o estado inicial é lido de forma segura
 * para SSR/ambiente sem window.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Mesmo breakpoint do CSS (`.row--mobile` em ui.css). */
export const useIsMobile = () => useMediaQuery('(max-width: 860px)');
